import type { LocaleCode } from "@/i18n/loadLocale";

/**
 * Public plan names, owned by the app's superadmin catalog.
 *
 * Copy never hard-codes a plan name: it uses {{planSolo}}, {{planPro}} and
 * {{planDeveloper}}, filled per language from the live catalog
 * (`/public/plans.json`, see server/livePlanNames.ts). The values below are the
 * fallback when the app cannot be reached; keep them equal to the app.
 */
export type PlanNameKey = "solo" | "pro" | "developer";
export type PlanNames = Record<PlanNameKey, string>;

export const PLAN_NAME_KEYS: readonly PlanNameKey[] = ["solo", "pro", "developer"];

/** App catalog key → marketing key. */
export const APP_PLAN_KEYS: Record<string, PlanNameKey> = {
  starter: "solo",
  professional: "pro",
  developer: "developer",
};

export const FALLBACK_PLAN_NAMES: Record<LocaleCode, PlanNames> = {
  sk: { solo: "Živnostník", pro: "Professional", developer: "Developer" },
  cs: { solo: "OSVČ", pro: "Professional", developer: "Developer" },
  da: { solo: "Enkeltmand", pro: "Professional", developer: "Developer" },
  en: { solo: "Solo", pro: "Professional", developer: "Developer" },
  de: { solo: "Einzelunternehmen", pro: "Professional", developer: "Developer" },
};

const PLACEHOLDERS: Record<PlanNameKey, string> = {
  solo: "{{planSolo}}",
  pro: "{{planPro}}",
  developer: "{{planDeveloper}}",
};

// Letters, digits, spaces and a few separators. Names land in copy that is
// sometimes rendered as HTML, so anything else from the catalog is rejected.
const SAFE_NAME = /^[\p{L}\p{N}][\p{L}\p{N} .&+'’-]{0,39}$/u;

export function isSafePlanName(value: unknown): value is string {
  return typeof value === "string" && SAFE_NAME.test(value.trim());
}

let live: Partial<Record<LocaleCode, Partial<PlanNames>>> = {};
let version = 0;

/** Current names for a language: live catalog over the bundled fallback. */
export function planNamesFor(lang: string): PlanNames {
  const base = FALLBACK_PLAN_NAMES[lang as LocaleCode] ?? FALLBACK_PLAN_NAMES.en;
  return { ...base, ...live[lang as LocaleCode] };
}

/** Bumps whenever live names change, so cached locale bundles can be rebuilt. */
export function planNamesVersion(): number {
  return version;
}

/**
 * Replace the live names. Unsafe or unknown entries are ignored.
 * Returns true when the effective names changed.
 */
export function setLivePlanNames(names: Partial<Record<string, Partial<Record<string, unknown>>>> | null | undefined): boolean {
  const next: Partial<Record<LocaleCode, Partial<PlanNames>>> = {};
  for (const lang of Object.keys(FALLBACK_PLAN_NAMES) as LocaleCode[]) {
    const row = names?.[lang];
    if (!row) continue;
    for (const key of PLAN_NAME_KEYS) {
      if (isSafePlanName(row[key])) (next[lang] ??= {})[key] = (row[key] as string).trim();
    }
  }
  if (JSON.stringify(next) === JSON.stringify(live)) return false;
  live = next;
  version += 1;
  return true;
}

function fill(text: string, names: PlanNames): string {
  let out = text;
  for (const key of PLAN_NAME_KEYS) out = out.split(PLACEHOLDERS[key]).join(names[key]);
  return out;
}

/** One string with the plan names for a language filled in. */
export function fillPlanNames(text: string, lang: string): string {
  return fill(text, planNamesFor(lang));
}

/** Fill plan placeholders in a locale bundle in place (strings in objects and arrays). */
export function applyPlanNamePlaceholders(root: unknown, lang: string): void {
  const names = planNamesFor(lang);
  const walk = (node: unknown): void => {
    if (Array.isArray(node)) {
      node.forEach((item, i) => {
        if (typeof item === "string") node[i] = fill(item, names);
        else walk(item);
      });
    } else if (node && typeof node === "object") {
      const record = node as Record<string, unknown>;
      for (const [key, value] of Object.entries(record)) {
        if (typeof value === "string") record[key] = fill(value, names);
        else walk(value);
      }
    }
  };
  walk(root);
}
